<template>
	<div style="width: 100%" class="d-flex justify-end">
		<!-- ENUM BUTTONS -->
		<div v-if="shouldShowAsButtons" style="width: 100%">
			<div v-if="term" style="max-width: 340px; color: var(--attr-label-color, grey); margin-bottom: 1px">
				{{ term + ":" }}
			</div>
			<div class="d-flex flex-wrap enum-button-row" style="gap: 8px">
				<v-tooltip
					v-for="(enumItemId, index) in filteredEnumerationItems"
					:key="enumItemId"
					:text="
						symbolsStore.term({
							id: graphStore.descriptor.enumItem.byId(enumItemId)?.refersTo,
							terminology: graphStore.descriptor.enumItem.byId(enumItemId)?.terminology,
						})
					"
					location="top"
					:disabled="!buttonTerm(enumItemId).endsWith('...')"
				>
					<template #activator="{ props }">
						<v-btn
							v-bind="props"
							:value="enumItemId"
							:active="value === enumItemId"
							size="large"
							variant="flat"
							:style="{
								maxWidth: '100%',
								flex: '1 0 auto',
								'--suggestion-chip-color': suggestionChipColor,
								'--btn-text-color': getTextColor(enumItemId),
								'--btn-bg-color': getButtonColor(enumItemId),
							}"
							:class="{
								'enum-btn': true,
								'enum-btn--active': value === enumItemId,
								'enum-btn--dimmed': hasActiveButtonSelection && value !== enumItemId,
								'enum-btn--custom-color': hasCustomColor(enumItemId),
								novaBotAnimationButton:
									questionsStore.tab === 'open' &&
									alternisNewStore.useNewAlternis &&
									alternisNewStore.isVoicebot &&
									value === enumItemId,
								suggestionAnimationButton:
									(questionsStore.tab === 'open' || isMetaQuestion) &&
									suggestionItems.some((i) => i.value === enumItemId) &&
									!hasCustomColor(enumItemId),
								suggestionAnimationButtonCustom:
									(questionsStore.tab === 'open' || isMetaQuestion) &&
									suggestionItems.some((i) => i.value === enumItemId) &&
									hasCustomColor(enumItemId),
							}"
							@click="() => setValue(value === enumItemId ? 'undefined' : enumItemId)"
						>
							<v-icon v-if="value === enumItemId" size="large" class="mr-1">mdi-check-circle</v-icon>
							<IconPreview :icon="getIcon(enumItemId)" />
							{{ buttonTerm(enumItemId) }}
						</v-btn>
					</template>
				</v-tooltip>
			</div>
			<!-- DEBUG BUTTONS -->
			<v-tooltip :text="symbolsStore.GUILabel('6fb730ba-a492-48c6-9eb2-64045fea443d')" location="top">
				<template v-slot:activator="{ props }">
					<v-btn
						v-if="
							domainStore.hasAccessRights(UserRole.Editor) &&
							(debugStore.altKeyHeld || debugStore.debugMode)
						"
						v-bind="props"
						size="small"
						color="blue"
						class="ml-1"
						icon="mdi-link"
						variant="text"
						@click="openEngineerDataobject"
					>
					</v-btn>
				</template>
			</v-tooltip>
			<v-tooltip :text="symbolsStore.GUILabel('dc327115-e1dc-4672-bba2-582e6742f284')" location="top">
				<template v-slot:activator="{ props }">
					<v-btn
						v-if="
							domainStore.hasAccessRights(UserRole.Editor) &&
							(debugStore.altKeyHeld || debugStore.debugMode)
						"
						v-bind="props"
						size="small"
						color="blue"
						class="ml-1"
						icon="mdi-link"
						variant="text"
						@click="openEngineerDatatype"
					>
					</v-btn>
				</template>
			</v-tooltip>
		</div>
		<!-- ENUM SELECT/DROPDOWN -->
		<v-select
			v-else
			class="question-input-field"
			v-model="displayedSelectValue"
			style="width: 100%"
			:class="{
				'question-input-field--multi': compProps.isArray,
				'enum-select--unknown-active': compProps.value === 'unknown' && !selectFocused,
				novaBotAnimationInput:
					questionsStore.tab === 'open' &&
					alternisNewStore.useNewAlternis &&
					alternisNewStore.isVoicebot &&
					hasSelectedEnumValue,
			}"
			variant="solo-filled"
			density="comfortable"
			clearable
			hide-details="auto"
			:items="filteredEnumerationItems"
			:item-value="(enumItemId: string) => enumItemId"
			:item-title="(enumItemId: string) => symbolsStore.term({id: graphStore.descriptor.enumItem.byId(enumItemId)?.refersTo, terminology: graphStore.descriptor.enumItem.byId(enumItemId)?.terminology })"
			:label="compProps.label"
			:placeholder="compProps.placeholder"
			:multiple="compProps.isArray"
			:autofocus="compProps.autofocus"
			@update:menu="onMenuUpdate"
			@focus="selectFocused = true"
			@blur="
				selectFocused = false;
				commitPendingArray();
			"
			@click:clear="onClearArray"
		>
			<template v-slot:item="{ props, item }">
				<v-list-item
					v-bind="props"
					:title="undefined"
					rounded
					:style="
						hasCustomColor(item.value)
							? {
									backgroundColor: getButtonColor(item.value),
									color: getTextColor(item.value),
									margin: '2px 4px',
							  }
							: { margin: '2px 4px' }
					"
				>
					<v-list-item-title class="d-flex align-center" style="gap: 6px">
						<v-icon v-if="isSelected(item.value)" size="large">mdi-check-circle</v-icon>
						<IconPreview :icon="getIcon(item.value)" />
						{{ item.title }}
					</v-list-item-title>
				</v-list-item>
			</template>
			<template v-if="!compProps.isArray" v-slot:selection="{ item }">
				<div class="d-flex align-center">
					<IconPreview :icon="getIcon(item.value)" />
					{{ item.title }}
				</div>
			</template>
			<template v-if="compProps.isArray" v-slot:chip="{ props: chipProps, item }">
				<v-chip
					v-bind="chipProps"
					:style="
						hasCustomColor(item.value)
							? {
									backgroundColor: getButtonColor(item.value),
									color: getTextColor(item.value),
							  }
							: {}
					"
				>
					<IconPreview :icon="getIcon(item.value)" />
					{{ item.title }}
				</v-chip>
			</template>
			<template v-if="showSuggestion" #details>
				<div class="suggestion-chips-row">
					<v-tooltip
						v-for="item in suggestionItems"
						:key="item.value"
						:text="item.text"
						location="top"
						:disabled="item.text.length <= 30"
					>
						<template v-slot:activator="{ props }">
							<v-chip
								v-bind="props"
								:class="{
									'suggestion-chip': true,
									'suggestion-chip--present': item.alreadyPresent,
									truncate: item.text.length > 30,
								}"
								:style="{ '--suggestion-chip-color': suggestionChipColor }"
								:prepend-icon="item.alreadyPresent ? 'mdi-check-circle' : 'mdi-creation'"
								density="comfortable"
								@click="applySuggestionItem(item)"
								>{{ truncate(item.text) }}</v-chip
							>
						</template>
					</v-tooltip>
				</div>
			</template>
			<template v-if="debugStore.altKeyHeld || (debugStore.debugMode && compProps.isArray)" #append>
				<v-tooltip :text="symbolsStore.GUILabel('6fb730ba-a492-48c6-9eb2-64045fea443d')" location="top">
					<template v-slot:activator="{ props }">
						<v-btn
							v-if="
								domainStore.hasAccessRights(UserRole.Editor) &&
								(debugStore.altKeyHeld || (debugStore.debugMode && compProps.isArray))
							"
							v-bind="props"
							size="small"
							color="blue"
							class="ml-1"
							icon="mdi-link"
							variant="text"
							@click="openEngineerDataobject"
						>
						</v-btn>
					</template>
				</v-tooltip>
				<v-tooltip :text="symbolsStore.GUILabel('dc327115-e1dc-4672-bba2-582e6742f284')" location="top">
					<template v-slot:activator="{ props }">
						<v-btn
							v-if="
								domainStore.hasAccessRights(UserRole.Editor) &&
								(debugStore.altKeyHeld || (debugStore.debugMode && compProps.isArray))
							"
							v-bind="props"
							size="small"
							color="blue"
							class="ml-1"
							icon="mdi-link"
							variant="text"
							@click="openEngineerDatatype"
						>
						</v-btn>
					</template>
				</v-tooltip>
			</template>
		</v-select>
	</div>
</template>

<script setup lang="ts">
import type { DescriptorDatatype, InstanceTask, LanguageCode } from "@lr-notitia/notitia-operator/schemas";
import { computed, PropType, onMounted, onUnmounted, ref } from "vue";
import { useGraphStore } from "../../../../store/graph";
import { useDomainStore } from "../../../../store/domain";
import { useQuestionsStore } from "../../../../store/questions";
import { useGptSuggestionsStore } from "../../../../store/gpt/suggestions";
import { useSymbolsStore } from "../../../../store/symbols";
import { useDebugStore } from "../../../../store/debug";
import { useAlternisNewStore } from "../../../../store/alternis_new";
import { useInstanceWorkspacesStore } from "../../../../store/i-workspace";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import type { ConceptView } from "@lr-notitia/notitia-model-store/schemas";
import IconPreview from "../IconPreview.vue";

const graphStore = useGraphStore();
const domainStore = useDomainStore();
const questionsStore = useQuestionsStore();
const gptSuggestionsStore = useGptSuggestionsStore();
const symbolsStore = useSymbolsStore();
const debugStore = useDebugStore();
const alternisNewStore = useAlternisNewStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();

const emits = defineEmits(["set"]);
const compProps = defineProps({
	datatype: {
		type: Object as PropType<DescriptorDatatype>,
		required: true,
	},
	value: {
		type: String as PropType<string | string[]>,
		required: true,
	},
	isArray: {
		type: Boolean,
		required: true,
	},
	label: {
		type: String,
		required: false,
	},
	placeholder: {
		type: String,
		required: false,
	},
	autofocus: {
		type: Boolean,
		required: false,
	},
	instanceTask: {
		type: Object as PropType<InstanceTask | undefined>,
		required: false,
		default: undefined,
	},
	attributeId: {
		type: String,
		required: true,
		default: "",
	},
	showAsButtons: {
		type: Boolean,
		required: true,
	},
	questionColor: {
		type: String,
		required: false,
		default: undefined,
	},
});
const attribute = computed(() => {
	return graphStore.instance.attribute.byId(compProps.attributeId);
});
// For array enums: buffer selections locally so the question stays open
// until the user closes the dropdown (blur), giving them a chance to
// select multiple items.
const pendingArrayValue = ref<string[] | null>(null);
const arrayMenuOpen = ref(false);
const selectFocused = ref(false);

function commitPendingArray() {
	if (pendingArrayValue.value === null) return;
	const v = pendingArrayValue.value;
	pendingArrayValue.value = null;
	if (v.length === 0) {
		setValue("undefined");
	} else {
		setValue(v);
	}
}

function onMenuUpdate(isOpen: boolean) {
	if (!compProps.isArray) return;
	arrayMenuOpen.value = isOpen;
	if (!isOpen) {
		commitPendingArray();
	}
}

const displayedValue = computed({
	get: () => {
		if (Array.isArray(compProps.value)) return undefined;
		if (compProps.value === "undefined" || compProps.value === "unknown") return undefined;
		return compProps.value;
	},
	set: (v) => {
		setValue(v);
	},
});
const displayedArrayValue = computed<string[]>({
	get: () => {
		if (pendingArrayValue.value !== null) return pendingArrayValue.value;
		if (!Array.isArray(compProps.value)) return [];
		return compProps.value;
	},
	set: (v) => {
		// Buffer the value instead of emitting immediately
		pendingArrayValue.value = Array.isArray(v) ? v : [];
	},
});
const displayedSelectValue = computed({
	get: () => {
		return compProps.isArray ? displayedArrayValue.value : displayedValue.value;
	},
	set: (v: any) => {
		if (compProps.isArray) {
			displayedArrayValue.value = Array.isArray(v) ? v : [];
			return;
		}
		displayedValue.value = v;
	},
});
const hasSelectedEnumValue = computed(() => {
	if (Array.isArray(displayedSelectValue.value)) return displayedSelectValue.value.length > 0;
	return Boolean(displayedSelectValue.value);
});
const conceptAttribute = computed(() => {
	return graphStore.concept.attribute.byId(attribute.value?.concept || "");
});

const shouldShowAsButtons = computed(() => {
	if (compProps.isArray) return false;
	if (!compProps.instanceTask) return compProps.showAsButtons;
	const templateTask = graphStore.template.task.byId(compProps.instanceTask.template);
	if (!templateTask) return compProps.showAsButtons;

	const conceptAttributeId = conceptAttribute.value?.id;
	if (!conceptAttributeId) return compProps.showAsButtons;

	// subset settings
	if (templateTask.context?.showAsButtons?.[conceptAttributeId] === true) return true;
	if (templateTask.context?.showAsDropDown?.[conceptAttributeId] === true) return false;

	// global settings
	const templateWorkspace = graphStore.template.workspace.byId(
		instanceWorkspacesStore.openedWorkspace?.template || "",
	);
	if (templateWorkspace?.context?.enumDisplay === "buttons") return true;
	if (templateWorkspace?.context?.enumDisplay === "dropdown") return false;

	// else dynamic
	return compProps.showAsButtons;
});

const filteredEnumerationItems = computed(() => {
	if (!compProps.instanceTask) return compProps.datatype.enumerationItems;
	const templateTask = graphStore.template.task.byId(compProps.instanceTask.template);
	if (!templateTask) return compProps.datatype.enumerationItems;

	const subset = templateTask.context?.datatypesubsets?.[conceptAttribute.value?.id || ""];
	if (subset === undefined) return compProps.datatype.enumerationItems;
	if (Array.isArray(subset)) {
		return subset.filter((enumItemId) => compProps.datatype.enumerationItems.includes(enumItemId));
	} else {
		return compProps.datatype.enumerationItems;
	}
});
const hasActiveButtonSelection = computed(() => {
	if (Array.isArray(compProps.value)) return false;
	if (compProps.value === "undefined") return false;
	if (compProps.value === "unknown") return true;
	return filteredEnumerationItems.value.includes(compProps.value as string);
});

const term = computed(() => {
	return symbolsStore.term({
		id: conceptAttribute?.value?.refersTo || "",
		terminology: conceptAttribute?.value?.terminology,
	});
});

type SuggestionItem = { value: string; text: string; alreadyPresent: boolean };

const suggestionArray = computed((): string[] => {
	if (!compProps.instanceTask || !attribute.value) return [];
	const raw: any = gptSuggestionsStore.suggestionData[attribute.value.id];
	if (raw === undefined || raw === null || raw === "") return [];
	if (Array.isArray(raw)) return [...new Set((raw as any[]).map(String))];
	if (typeof raw === "string") return [raw];
	return [];
});

const suggestionItems = computed((): SuggestionItem[] => {
	return suggestionArray.value
		.filter((v) => v !== "undefined" && v !== "unknown")
		.map((v): SuggestionItem | null => {
			if (v === "?") {
				return { value: "?", text: "?", alreadyPresent: compProps.value === "unknown" };
			}
			if (isEnumItemId(v)) {
				const text = buttonTerm(v);
				return { value: v, text: text || v, alreadyPresent: isSelected(v) };
			}
			const matchedId = filteredEnumerationItems.value.find((enumItemId) => {
				const enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
				return (
					symbolsStore.term({
						id: enumItem?.refersTo,
						terminology: enumItem?.terminology,
					}) === v
				);
			});
			if (matchedId) {
				return { value: matchedId, text: v, alreadyPresent: isSelected(matchedId) };
			}
			return null;
		})
		.filter((item): item is SuggestionItem => item !== null);
});

const isMetaQuestion = computed(() => {
	if (!compProps.instanceTask) return false;
	return graphStore.template.task.byId(compProps.instanceTask.template)?.context?.["metaQuestion"] === true;
});

const showSuggestion = computed(() => {
	return suggestionItems.value.length > 0 && (questionsStore.tab === "open" || isMetaQuestion.value);
});

const suggestionChipColor = computed(() => {
	return compProps.questionColor || "#4db6ac";
});

function getIcon(enumItemId: string) {
	const enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
	return enumItem?.context?.["icon"];
}

function isSelected(enumItemId: string): boolean {
	if (compProps.isArray && pendingArrayValue.value !== null) return pendingArrayValue.value.includes(enumItemId);
	if (Array.isArray(compProps.value)) return compProps.value.includes(enumItemId);
	return compProps.value === enumItemId;
}

function setValue(v: any) {
	if (Array.isArray(v)) {
		if (v.every((item: any) => isEnumItemId(item) || item === "undefined" || item === "unknown")) {
			emits("set", v);
			copyValueToClipboard(v);
		}
	} else if (isEnumItemId(v) || v === "undefined" || v === "unknown") {
		emits("set", v);
		copyValueToClipboard(v);
	}
}

function onClearArray() {
	if (compProps.isArray) {
		pendingArrayValue.value = null;
	}
	setValue("undefined");
}

function applySuggestionItem(item: SuggestionItem) {
	if (item.value === "?") {
		emits("set", item.alreadyPresent ? "undefined" : "unknown");
		return;
	}
	if (item.alreadyPresent) {
		if (compProps.isArray && Array.isArray(compProps.value)) {
			const remaining = compProps.value.filter((v) => v !== item.value);
			emits("set", remaining.length === 0 ? "undefined" : remaining);
		} else {
			emits("set", "undefined");
		}
		return;
	}
	if (compProps.isArray) {
		if (compProps.value === "undefined" || compProps.value === "unknown" || !Array.isArray(compProps.value)) {
			emits("set", [item.value]);
		} else {
			emits("set", [...compProps.value, item.value]);
		}
	} else {
		emits("set", item.value);
	}
}

function getButtonColor(enumItemId: string) {
	// task specific color
	if (compProps.instanceTask) {
		const templateTask = graphStore.template.task.byId(compProps.instanceTask.template);
		const conceptAttributeId = conceptAttribute.value?.id;
		if (templateTask && conceptAttributeId) {
			const color = templateTask.context?.datatypesubsetsButtonColors?.[conceptAttributeId]?.[enumItemId];
			if (color) return color;
		}
	}
	// datatype specific color
	let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
	if (enumItem && enumItem.context?.["color"]) {
		return enumItem.context["color"];
	}
	// Use CSS variable for theme-aware default color
	return "var(--enum-btn-default-bg)";
}

function hasCustomColor(enumItemId: string): boolean {
	// Check task specific color
	if (compProps.instanceTask) {
		const templateTask = graphStore.template.task.byId(compProps.instanceTask.template);
		const conceptAttributeId = conceptAttribute.value?.id;
		if (templateTask && conceptAttributeId) {
			const color = templateTask.context?.datatypesubsetsButtonColors?.[conceptAttributeId]?.[enumItemId];
			if (color) return true;
		}
	}
	// Check datatype specific color
	let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
	if (enumItem && enumItem.context?.["color"]) {
		return true;
	}
	return false;
}

function getTextColor(enumItemId: string): string {
	const bgColor = getButtonColor(enumItemId);
	if (!bgColor || bgColor === "") return "inherit";

	// Handle CSS variable for default button color
	if (bgColor === "var(--enum-btn-default-bg)") {
		return "var(--enum-btn-default-text)";
	}

	// Parse color and calculate luminance
	const getLuminance = (color: string): number => {
		// Handle hex colors
		if (color.startsWith("#")) {
			const hex = color.replace("#", "");
			const r = parseInt(hex.substring(0, 2), 16) / 255;
			const g = parseInt(hex.substring(2, 4), 16) / 255;
			const b = parseInt(hex.substring(4, 6), 16) / 255;
			return 0.299 * r + 0.587 * g + 0.114 * b;
		}
		// Handle named colors - default to mid-range for unknown
		if (color === "grey" || color === "gray") return 0.5;
		return 0.5; // Default for unparseable colors
	};

	const luminance = getLuminance(bgColor);
	// Use white text for dark backgrounds, black for light
	return luminance < 0.5 ? "white" : "black";
}

function buttonTerm(enumItemId: string) {
	let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
	if (!enumItem) return "no enumItem";

	let symbol = symbolsStore.byId(enumItem.refersTo);

	// selected lang term
	let term = symbolsStore.term({
		id: symbol?.id,
		terminology: enumItem.terminology,
	});
	// english term
	if (!term)
		term = symbolsStore.term({
			id: symbol?.id,
			terminology: enumItem.terminology,
			lang: "en",
		});
	// other lang term
	if (!term && symbol) {
		let otherLang: LanguageCode = Object.keys(symbol?.terminology).find(
			(lang) => lang !== "en" && lang !== domainStore.languagePreference,
		) as LanguageCode;
		if (otherLang)
			term = symbolsStore.term({
				id: symbol?.id,
				terminology: enumItem.terminology,
				lang: otherLang,
			});
	}

	return term || "";
}

function isEnumItemId(v: any): boolean {
	return graphStore.descriptor.enumItem.byId(v) !== undefined;
}

function openEngineerDatatype() {
	const protocol = window.location.protocol === "http:" ? "http" : "https";
	const host = `engineer.notitia.dev`;
	const { id: domainId } = domainStore.domain;
	const url = `${protocol}://${host}/${domainId}/datatypes?datatype-id=${compProps.datatype.id}`;
	window.open(url, "_blank");
}
function getView(conceptDataobjectId: string): ConceptView | undefined {
	const views = Object.values(graphStore.graph.nodes).filter((n) => n.type === "view") as ConceptView[];

	let visited: string[] = [];
	function traverseDataobject(id: string): boolean {
		if (id === conceptDataobjectId) return true;
		if (visited.includes(id)) return false;
		visited.push(id);
		let conceptDataobject = graphStore.concept.dataobject.byId(id);
		if (!conceptDataobject) return false;
		return (
			conceptDataobject.hasA.some((id) => traverseDataobject(id)) ||
			conceptDataobject.composedBy.some((id) => traverseDataobject(id))
		);
	}

	for (let view of views) {
		if (view.entryNodes.some((id) => traverseDataobject(id))) return view;
	}
}
function openEngineerDataobject(event: MouseEvent) {
	event.stopPropagation();
	const protocol = window.location.protocol === "http:" ? "http" : "https";
	const host = `engineer.notitia.dev`;
	const { id: domainId } = domainStore.domain;

	let conceptAttribute = graphStore.concept.attribute.byId(attribute.value?.concept || "");
	let conceptDataobject = graphStore.concept.dataobject.byId(conceptAttribute?.partOf || "");
	if (!conceptDataobject) return;
	let view: ConceptView | undefined = getView(conceptDataobject.id);
	if (!view) return;

	const url = `${protocol}://${host}/${domainId}/information-model/${view.id}?dataobject-id=${conceptDataobject.id}`;
	window.open(url, "_blank");
}
const shouldCopyToClipboard = computed(() => {
	return compProps.datatype.context?.copyToClipboard === true;
});

function copyValueToClipboard(v: any) {
	if (!shouldCopyToClipboard.value) return;
	const values = Array.isArray(v) ? v : [v];
	const text = values
		.filter((item: string) => item !== "undefined" && item !== "unknown")
		.map((item: string) => {
			const enumItem = graphStore.descriptor.enumItem.byId(item);
			if (!enumItem) return item;
			return symbolsStore.term({ id: enumItem.refersTo, terminology: enumItem.terminology }) || item;
		})
		.join(", ");
	if (text)
		navigator.clipboard.writeText(text.includes("-") ? text.split("-")[text.split("-").length - 1].trim() : text);
}

function truncate(text: string, length = 30) {
	if (text.length < length) return text;
	return text.slice(0, length) + "...";
}

onMounted(() => {
	window.addEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.addEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});
onUnmounted(() => {
	window.removeEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.removeEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});
</script>

<style scoped lang="scss">
:deep(.question-input-field:not(.question-input-field--multi) .v-field) {
	min-height: 56px;
}

:deep(.question-input-field:not(.question-input-field--multi) .v-field__input) {
	padding-top: 24px;
	padding-bottom: 6px;
}

:deep(.question-input-field--multi .v-field) {
	height: auto !important;
	min-height: 56px;
}

:deep(.question-input-field--multi .v-field__input) {
	align-items: flex-start;
	display: flex;
	flex-wrap: wrap;
	overflow: visible;
	padding-bottom: 8px;
	padding-top: 28px;
	row-gap: 4px;
}

:deep(.v-input__details) {
	overflow: visible;
	padding-bottom: 4px;
}

.suggestion-chips-row {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	padding: 6px 4px 2px;
}

.suggestion-chip {
	margin: 0;
	animation: colorBreathing 2s infinite;
}

.suggestion-chip--present {
	animation: none;
	box-shadow: none;
	opacity: 0.7;
}

/* Theme-aware default button background */
.v-theme--light {
	--enum-btn-default-bg: #e5e5e5;
	--enum-btn-default-bg-hover: #bababa;
	--enum-btn-default-bg-active: #999;
	--enum-btn-default-text: black;
}

.v-theme--dark {
	--enum-btn-default-bg: #595959;
	--enum-btn-default-bg-hover: #6a6a6a;
	--enum-btn-default-bg-active: #7a7a7a;
	--enum-btn-default-text: white;
}

@keyframes colorBreathing {
	0% {
		box-shadow: 0 0 3px 1px var(--suggestion-chip-color, #4db6ac);
	}
	50% {
		box-shadow: 0 0 5px 2px var(--suggestion-chip-color, #4db6ac);
	}
	100% {
		box-shadow: 0 0 3px 1px var(--suggestion-chip-color, #4db6ac);
	}
}

.truncate {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.novaBotAnimationInput ::v-deep .v-input__control {
	transition: box-shadow 0.3s ease, transform 0.3s ease;
	animation: questionColorGlow 3s ease-in-out;
}
.novaBotAnimationButton {
	transition: background-color 0.3s ease, transform 0.3s ease;
	animation: questionColorGlow 3s ease-in-out;
}

.suggestionAnimationButton {
	animation: questionColorGlow 2s ease-in-out infinite;
}

.suggestionAnimationButtonCustom {
	animation: customColorGlow 2s ease-in-out infinite;
}

@keyframes customColorGlow {
	0% {
		box-shadow: 0 0 4px 2px var(--btn-bg-color);
		transform: scale(1);
		filter: brightness(1);
	}
	50% {
		box-shadow: 0 0 8px 4px var(--btn-bg-color);
		transform: scale(1.05);
		filter: brightness(1.1);
	}
	100% {
		box-shadow: 0 0 4px 2px var(--btn-bg-color);
		transform: scale(1);
		filter: brightness(1);
	}
}

@keyframes questionColorGlow {
	0% {
		box-shadow: 0 0 4px 2px var(--suggestion-chip-color, #4db6ac);
		transform: scale(1);
	}
	50% {
		box-shadow: 0 0 8px 4px var(--suggestion-chip-color, #4db6ac);
		transform: scale(1.05);
	}
	100% {
		box-shadow: 0 0 4px 2px var(--suggestion-chip-color, #4db6ac);
		transform: scale(1);
	}
}

/* Tonal button with outline styling */
.enum-btn {
	outline: 1px solid rgba(0, 0, 0, 0.6);
	outline-offset: -1px;
	color: var(--btn-text-color) !important;
	background-color: var(--btn-bg-color) !important;
	transition: transform 0.1s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.1s ease-out, outline 0.1s ease-out,
		filter 0.15s ease-out, opacity 0.15s ease-out;
	height: auto !important;
	min-height: 44px;
	padding: 8px 16px !important;

	/* Allow multi-line text */
	:deep(.v-btn__content) {
		white-space: normal !important;
		word-wrap: break-word;
		text-align: center;
		line-height: 1.3;
	}

	/* Remove Vuetify overlays that darken the button */
	:deep(.v-btn__overlay) {
		opacity: 0 !important;
	}

	&:hover {
		outline: 3px solid rgba(0, 0, 0, 0.6);
		outline-offset: -1px;
		/* Use darker shade for hover - works with custom colors via color-mix */
		background-color: color-mix(in srgb, var(--btn-bg-color) 85%, black) !important;
	}
}

.enum-btn--dimmed {
	filter: saturate(0.45);
	opacity: 0.82;
}

.enum-select--unknown-active {
	filter: saturate(0.45);
	opacity: 0.55;
	transition: filter 0.15s ease-out, opacity 0.15s ease-out;
}

/* Override hover for default grey buttons to use predefined hover color */
.enum-btn:not(.enum-btn--custom-color):hover {
	background-color: var(--enum-btn-default-bg-hover) !important;
}

.v-theme--dark .enum-btn:hover {
	outline: 3px solid rgba(255, 255, 255, 0.7);
	/* Use lighter shade for hover in dark mode */
	background-color: color-mix(in srgb, var(--btn-bg-color) 85%, white) !important;
}

.v-theme--dark .enum-btn:not(.enum-btn--custom-color):hover {
	background-color: var(--enum-btn-default-bg-hover) !important;
}

.enum-btn--active {
	outline: none;
	transform: scale(1.05);
	/* Use even darker shade for active state */
	background-color: color-mix(in srgb, var(--btn-bg-color) 75%, black) !important;
	box-shadow: inset 0 0 0 3px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.25);
}

/* Override active for default grey buttons to use predefined active color */
.enum-btn--active:not(.enum-btn--custom-color) {
	background-color: var(--enum-btn-default-bg-active) !important;
}

.v-theme--dark .enum-btn--active {
	/* Use lighter shade for active in dark mode */
	background-color: color-mix(in srgb, var(--btn-bg-color) 75%, white) !important;
	box-shadow: inset 0 0 0 3px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 0, 0, 0.25);
}

.v-theme--dark .enum-btn--active:not(.enum-btn--custom-color) {
	background-color: var(--enum-btn-default-bg-active) !important;
}
</style>
